/**
 * F-136 — saving the design theme's IMAGES.
 *
 * The style sheet and the style references are the two places on a board
 * whose pictures are not elements: a generated page lives in the `design` map
 * (a shot is only promoted to a real element when someone places it) and a
 * reference frame never becomes one at all. So neither is reachable from the
 * F-135 element download, and an animator who wanted the bar-chart page had to
 * screenshot the panel. This module gives both the same one-click save.
 *
 * Only the NAMING lives here — the fetch-and-save is core/download-asset,
 * shared with the element download so the two can never disagree about how the
 * bytes are fetched.
 */
import { withExtensionFor, type DesignRefInfo, type DesignShotInfo } from "@kola/shared";

import { downloadAsset } from "../../core/download-asset.js";

/**
 * A label as a file-name stem: lowercase, punctuation collapsed to single
 * hyphens. "Map — colour & texture" → "map-colour-texture". Non-ASCII is kept
 * (a Korean page name saves under its own name — every OS Kola runs on takes
 * it), which is why this strips a specific set rather than keeping `[a-z0-9]`:
 * the latter would name every Korean page "design-".
 */
export function slugForFileName(label: string): string {
  return label
    .trim()
    .toLowerCase()
    // Everything a file name must not carry, plus the whitespace and
    // punctuation that reads as a word break.
    .replaceAll(/[\\/:*?"<>|]+/g, " ")
    .replaceAll(/[\s_,.—–&]+/g, "-")
    .replaceAll(/-{2,}/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

/**
 * What one style-sheet page saves as. The page's own name when the run
 * recorded one ("Bar chart" → `design-bar-chart.webp`) — that is the only
 * thing that tells ten near-identical thumbnails apart on disk. `position` is
 * the shot's 1-based place in the gallery and names the pages of a run that
 * predates the catalogue, where the label is absent.
 */
export function designShotFileName(
  shot: DesignShotInfo,
  position: number,
  blobType: string,
): string {
  const slug = shot.label === undefined ? "" : slugForFileName(shot.label);
  const stem = slug === "" ? `key-visual-${position}` : slug;
  return withExtensionFor(`design-${stem}`, blobType);
}

/**
 * What one style reference saves as. References carry a note, not a name, and
 * a note is a sentence ("this is the shadow quality I mean") rather than a
 * file name — so they are numbered by their place in the strip, which is what
 * the user points at when they say "the third one".
 */
export function designRefFileName(position: number, blobType: string): string {
  return withExtensionFor(`design-reference-${position}`, blobType);
}

/** Save one generated style-sheet page. `position` is its 1-based place. */
export async function downloadDesignShot(shot: DesignShotInfo, position: number): Promise<void> {
  await downloadAsset(shot.assetId, (blobType) => designShotFileName(shot, position, blobType));
}

/** Save one style-reference frame. `position` is its 1-based place. */
export async function downloadDesignRef(ref: DesignRefInfo, position: number): Promise<void> {
  await downloadAsset(ref.assetId, (blobType) => designRefFileName(position, blobType));
}
